/**
 * Tier configuration and validation for backtest features.
 * Mirrors the server-side tier limits.
 */

// Tier configuration: billing_tier -> max years of backtest history
const TIER_BACKTEST_LIMITS = {
  'free': 3,
  'starter': 7,
  'pro': 15,
  'ultimate': 25
};

// Storage limits per tier
export const TIER_STORAGE_LIMITS = {
  'free': {
    screens: 5,
    watchlists: 5,
    backtests: 10,
    backtest_retention_days: 7
  },
  'starter': {
    screens: 25,
    watchlists: 25,
    backtests: 100,
    backtest_retention_days: 90
  },
  'pro': {
    screens: 100,
    watchlists: 100,
    backtests: 500,
    backtest_retention_days: 365
  },
  'ultimate': {
    screens: 1000,
    watchlists: 500,
    backtests: 2000,
    backtest_retention_days: null // unlimited
  }
};

/**
 * Get maximum backtest history years for a billing tier.
 * @param {string} billingTier - User's billing tier
 * @returns {number} Maximum number of years of backtest history allowed
 */
export function getMaxBacktestYears(billingTier) {
  return TIER_BACKTEST_LIMITS[billingTier] || TIER_BACKTEST_LIMITS['free'];
}

/**
 * Validate that backtest date range is within tier limits.
 * Restricts users to only backtest the MOST RECENT N years from TODAY.
 * @param {Date} startDate - Backtest start date
 * @param {Date} endDate - Backtest end date
 * @param {string} billingTier - User's billing tier
 * @returns {object} { isValid: boolean, errorMessage: string }
 */
export function validateBacktestDateRange(startDate, endDate, billingTier) {
  if (!startDate || !endDate) {
    return {
      isValid: false,
      errorMessage: 'Start and end dates are required'
    };
  }

  if (startDate >= endDate) {
    return {
      isValid: false,
      errorMessage: 'Start date must be before end date'
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of day

  // Convert all dates to YYYY-MM-DD strings to avoid timezone issues
  const startDateStr = startDate.toISOString().split('T')[0];
  const endDateStr = endDate.toISOString().split('T')[0];
  const todayStr = today.toISOString().split('T')[0];

  // Check if end_date is in the future (string comparison works correctly)
  if (endDateStr > todayStr) {
    return {
      isValid: false,
      errorMessage: `End date cannot be in the future. End date: ${endDateStr}, Today: ${todayStr}.`
    };
  }

  const maxYears = getMaxBacktestYears(billingTier);
  const maxStartDate = getMaxStartDate(today, billingTier);
  const maxStartDateStr = maxStartDate.toISOString().split('T')[0];
  const tierName = getTierDisplayName(billingTier);

  // Check if start_date is too old (before earliest allowed)
  // Use string comparison to avoid timezone issues with Date objects
  if (startDateStr < maxStartDateStr) {
    return {
      isValid: false,
      errorMessage: `${tierName} can only backtest the last ${maxYears} years from today. Earliest allowed start date: ${maxStartDateStr}. Requested start date: ${startDateStr}.`,
      tier: billingTier,
      tierDisplayName: tierName,
      maxYears,
      maxStartDate
    };
  }

  return {
    isValid: true,
    errorMessage: ''
  };
}

/**
 * Get user-friendly name for billing tier.
 * @param {string} billingTier - User's billing tier
 * @returns {string} Display name for the tier
 */
export function getTierDisplayName(billingTier) {
  const tierNames = {
    'free': 'Free',
    'starter': 'Premium Starter',
    'pro': 'Premium Pro',
    'ultimate': 'Premium Ultimate'
  };
  return tierNames[billingTier] || billingTier;
}

function firstDayOfNextMonth(dt) {
  const year = dt.getUTCFullYear();
  const month = dt.getUTCMonth();
  return new Date(Date.UTC(year, month + 1, 1));
}

/**
 * Get the maximum start date allowed for a given end date and tier.
 * Calculates exactly N years ago (same month and day) and rounds forward to
 * the first day of the following month to align with monthly data boundaries.
 * @param {Date} endDate - The end date for the backtest
 * @param {string} billingTier - User's billing tier
 * @returns {Date} The earliest allowed start date
 */
export function getMaxStartDate(endDate, billingTier) {
  const maxYears = getMaxBacktestYears(billingTier);
  const maxStartDate = new Date(endDate);
  maxStartDate.setFullYear(maxStartDate.getFullYear() - maxYears);

  // Handle edge case: if end date is Feb 29 and N years ago isn't a leap year
  if (endDate.getMonth() === 1 && endDate.getDate() === 29) {
    const testDate = new Date(maxStartDate.getFullYear(), 1, 29);
    if (testDate.getDate() !== 29) {
      // Not a leap year, set to Feb 28
      maxStartDate.setDate(28);
    }
  }

  return firstDayOfNextMonth(maxStartDate);
}

/**
 * Get the earliest screen date allowed for a billing tier.
 * Screens are point-in-time, so we don't round to month boundaries.
 * @param {Date} referenceDate - Date to compute the limit from (defaults to today)
 * @param {string} billingTier - User's billing tier
 * @returns {Date} Earliest allowed screen date (UTC midnight)
 */
export function getEarliestScreenDate(referenceDate = new Date(), billingTier) {
  const maxYears = getMaxBacktestYears(billingTier);
  const ref = new Date(referenceDate);
  const year = ref.getUTCFullYear() - maxYears;
  const month = ref.getUTCMonth();
  const day = ref.getUTCDate();
  let earliest = new Date(Date.UTC(year, month, day));

  if (ref.getUTCMonth() === 1 && ref.getUTCDate() === 29 && earliest.getUTCMonth() !== 1) {
    earliest = new Date(Date.UTC(year, 1, 28));
  }

  return earliest;
}

/**
 * Get storage limits for a billing tier.
 * @param {string} billingTier - User's billing tier
 * @returns {object} Storage limits with keys: screens, watchlists, backtests, backtest_retention_days
 */
export function getStorageLimits(billingTier) {
  return TIER_STORAGE_LIMITS[billingTier] || TIER_STORAGE_LIMITS['free'];
}

/**
 * Get maximum number of screens allowed for a billing tier.
 * @param {string} billingTier - User's billing tier
 * @returns {number} Maximum screens
 */
export function getScreenLimit(billingTier) {
  return getStorageLimits(billingTier).screens;
}

/**
 * Get maximum number of watchlists allowed for a billing tier.
 * @param {string} billingTier - User's billing tier
 * @returns {number} Maximum watchlists
 */
export function getWatchlistLimit(billingTier) {
  return getStorageLimits(billingTier).watchlists;
}

/**
 * Get maximum number of saved backtests allowed for a billing tier.
 * @param {string} billingTier - User's billing tier
 * @returns {number} Maximum backtests
 */
export function getBacktestLimit(billingTier) {
  return getStorageLimits(billingTier).backtests;
}

/**
 * Get backtest retention period in days for a billing tier.
 * @param {string} billingTier - User's billing tier
 * @returns {number|null} Number of days to retain backtests, or null for unlimited
 */
export function getBacktestRetentionDays(billingTier) {
  return getStorageLimits(billingTier).backtest_retention_days;
}
